import { proxy, ref } from 'valtio/vanilla';
import { EventsController } from './EventsController.js';
import { ModalController } from './ModalController.js';
import { CoreHelperUtil } from '../utils/CoreHelperUtil.js';
import { NetworkUtil } from '@web3modal/common';
import { ChainController } from './ChainController.js';
import { PublicStateController } from './PublicStateController.js';
// -- State --------------------------------------------- //
const state = proxy({
    supportsAllNetworks: true,
    isDefaultCaipNetwork: false,
    smartAccountEnabledNetworks: []
});
// -- Controller ---------------------------------------- //
export const NetworkController = {
    state,
    replaceState(newState) {
        if (!newState) {
            return;
        }
        Object.assign(state, ref(newState));
    },
    subscribeKey(property, callback) {
        let prev = undefined;
        return ChainController.subscribeChainProp('networkState', networkState => {
            if (networkState) {
                const nextValue = networkState[property];
                if (prev !== nextValue) {
                    prev = nextValue;
                    callback(nextValue);
                }
            }
        });
    },
    _getClient() {
        return ChainController.getNetworkControllerClient();
    },
    initializeDefaultNetwork() {
        const networks = this.getRequestedCaipNetworks();
        if (networks.length > 0) {
            this.setCaipNetwork(networks[0]);
        }
    },
    setDefaultCaipNetwork(caipNetwork) {
        if (caipNetwork) {
            ChainController.setCaipNetwork(caipNetwork.chain, caipNetwork, true);
            ChainController.setChainNetworkData(caipNetwork.chain, { isDefaultCaipNetwork: true });
            PublicStateController.set({ selectedNetworkId: caipNetwork.id });
        }
    },
    setActiveCaipNetwork(caipNetwork) {
        if (!caipNetwork) {
            return;
        }
        ChainController.setActiveCaipNetwork(caipNetwork);
        ChainController.setChainNetworkData(caipNetwork.chain, { caipNetwork });
        PublicStateController.set({
            activeChain: caipNetwork.chain,
            selectedNetworkId: caipNetwork?.id
        });
        if (!ChainController.state.chains.get(caipNetwork.chain)?.networkState?.allowUnsupportedChain) {
            const isSupported = this.checkIfSupportedNetwork();
            if (!isSupported) {
                this.showUnsupportedChainUI();
            }
        }
    },
    setCaipNetwork(caipNetwork) {
        if (!caipNetwork) {
            return;
        }
        if (!caipNetwork?.chain) {
            throw new Error('chain is required to set active network');
        }
        ChainController.setCaipNetwork(caipNetwork?.chain, caipNetwork);
        if (!ChainController.state.chains.get(caipNetwork.chain)?.networkState?.allowUnsupportedChain) {
            const isSupported = this.checkIfSupportedNetwork();
            if (!isSupported) {
                this.showUnsupportedChainUI();
            }
        }
    },
    setRequestedCaipNetworks(requestedNetworks, chain) {
        ChainController.setChainNetworkData(chain, { requestedCaipNetworks: requestedNetworks });
    },
    setAllowUnsupportedChain(allowUnsupportedChain, chain) {
        ChainController.setChainNetworkData(chain || ChainController.state.activeChain, {
            allowUnsupportedChain
        });
    },
    setSmartAccountEnabledNetworks(smartAccountEnabledNetworks, chain) {
        ChainController.setChainNetworkData(chain, { smartAccountEnabledNetworks });
    },
    getRequestedCaipNetworks(chainToFilter) {
        let chainAdapters = undefined;
        if (!ChainController.state.activeChain) {
            throw new Error('activeChain is required to get requested networks');
        }
        if (chainToFilter) {
            const chain = chainToFilter;
            if (!chain) {
                throw new Error('chain is required to get requested networks');
            }
            chainAdapters = [chain];
        }
        else {
            const chains = [...ChainController.state.chains.keys()];
            chainAdapters = chains;
        }
        const approvedIds = [];
        const requestedNetworks = [];
        chainAdapters.forEach((chn) => {
            if (ChainController.state.chains.get(chn)?.networkState?.approvedCaipNetworkIds) {
                approvedIds.push(...(ChainController.state.chains.get(chn)?.networkState?.approvedCaipNetworkIds || []));
            }
            if (ChainController.state.chains.get(chn)?.networkState?.requestedCaipNetworks) {
                requestedNetworks.push(...(ChainController.state.chains.get(chn)?.networkState?.requestedCaipNetworks || []));
            }
        });
        const sortedNetworks = CoreHelperUtil.sortRequestedNetworks(approvedIds, requestedNetworks);
        return sortedNetworks;
    },
    async switchActiveNetwork(network) {
        const networkControllerClient = network
            ? ChainController.state.chains.get(network.chain)?.networkControllerClient
            : undefined;
        if (!networkControllerClient) {
            throw new Error('networkControllerClient not found for given network object');
        }
        ChainController.setActiveCaipNetwork(network);
        await networkControllerClient?.switchCaipNetwork(network);
        if (network) {
            EventsController.sendEvent({
                type: 'track',
                event: 'SWITCH_NETWORK',
                properties: { network: network.id }
            });
        }
    },
    getApprovedCaipNetworkIds(chainToFilter) {
        if (chainToFilter) {
            const chain = chainToFilter;
            if (!chain) {
                throw new Error('chain is required to get approved network IDs');
            }
            return ChainController.state.chains.get(chain)?.networkState?.approvedCaipNetworkIds;
        }
        const allCaipNetworkIds = [];
        Object.values(ChainController.state.chains).forEach(adapter => {
            if (adapter.networkState.approvedCaipNetworkIds) {
                allCaipNetworkIds.push(...(adapter.networkState?.approvedCaipNetworkIds || []));
            }
        });
        return allCaipNetworkIds;
    },
    async setApprovedCaipNetworksData(chain) {
        const networkControllerClient = ChainController.getNetworkControllerClient();
        const data = await networkControllerClient.getApprovedCaipNetworksData();
        if (!chain) {
            throw new Error('chain is required to set approved network data');
        }
        ChainController.setChainNetworkData(chain, {
            approvedCaipNetworkIds: data?.approvedCaipNetworkIds,
            supportsAllNetworks: data?.supportsAllNetworks || false
        });
    },
    checkIfSupportedNetwork() {
        const chain = ChainController.state.activeChain;
        if (!chain) {
            return false;
        }
        const activeCaipNetwork = ChainController.state.chains.get(chain)?.networkState?.caipNetwork;
        const requestedCaipNetworks = this.getRequestedCaipNetworks();
        if (!requestedCaipNetworks.length) {
            return true;
        }
        return requestedCaipNetworks?.some(network => network.id === activeCaipNetwork?.id);
    },
    checkIfSmartAccountEnabled() {
        const networkId = NetworkUtil.caipNetworkIdToNumber(state.caipNetwork?.id);
        const activeChain = ChainController.state.activeChain;
        if (!activeChain) {
            throw new Error('activeChain is required to check if smart account is enabled');
        }
        if (!networkId) {
            return false;
        }
        const smartAccountEnabledNetworks = ChainController.getNetworkProp('smartAccountEnabledNetworks');
        return Boolean(smartAccountEnabledNetworks?.includes(networkId));
    },
    resetNetwork() {
        const chain = ChainController.state.activeChain;
        if (!chain) {
            throw new Error('chain is required to reset network');
        }
        ChainController.setChainNetworkData(chain, {
            approvedCaipNetworkIds: undefined,
            supportsAllNetworks: true,
            smartAccountEnabledNetworks: []
        });
    },
    getSupportsAllNetworks() {
        const chain = ChainController.state.activeChain;
        if (!chain) {
            throw new Error('chain is required to check if network supports all networks');
        }
        return ChainController.state.chains.get(chain)?.networkState?.supportsAllNetworks;
    },
    showUnsupportedChainUI() {
        setTimeout(() => {
            ModalController.open({ view: 'UnsupportedChain' });
        }, 300);
    }
};
//# sourceMappingURL=NetworkController.js.map